import { motion } from "framer-motion";
import { Reveal } from "@/lib/motion";
import { CalendarClock, Radar, Siren, ShieldCheck, TrendingUp, Wallet, LayoutDashboard, Sparkles, ArrowRight } from "lucide-react";

const AGENT_IMG =
  "https://images.pexels.com/photos/7641896/pexels-photo-7641896.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=760&w=940";
const SECURITY_IMG =
  "https://images.pexels.com/photos/12304330/pexels-photo-12304330.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=760&w=940";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const Feature = ({ icon: Icon, title, children }) => (
  <div className="flex gap-4">
    <div className="w-10 h-10 rounded-[10px] bg-[#292414] flex items-center justify-center shrink-0 mt-0.5">
      <Icon className="w-5 h-5 text-[#D4AF37]" />
    </div>
    <div>
      <h4 className="font-head font-bold text-[#F8FAFC] text-base">{title}</h4>
      <p className="font-body text-[#94A3B8] text-sm leading-relaxed mt-1">{children}</p>
    </div>
  </div>
);

const ClippedImage = ({ src, alt }) => (
  <div className="relative rounded-[20px] overflow-hidden border border-[#1E293B] h-[420px] lg:h-[540px]">
    <motion.img
      src={src}
      alt={alt}
      initial={{ scale: 1.12 }}
      whileInView={{ scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      className="w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-tr from-[#050914]/80 via-transparent to-[#D4AF37]/10 mix-blend-multiply" />
    <div className="absolute inset-0 ring-1 ring-inset ring-white/5 rounded-[20px]" />
  </div>
);

export const DualAudience = () => {
  return (
    <div>
      {/* Agents */}
      <section id="agents" className="relative py-24 lg:py-32 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal className="order-2 lg:order-1">
            <ClippedImage src={AGENT_IMG} alt="Real estate agent showing a home" />
          </Reveal>
          <div className="order-1 lg:order-2">
            <Reveal><span className="kicker">For Real Estate Agents</span></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-head font-black tracking-tight text-[#F8FAFC] text-4xl lg:text-5xl mt-5 leading-[1.05]">
                Show every property <span className="text-[#D4AF37]">with backup.</span>
              </h2>
            </Reveal>
            <div className="mt-10 space-y-7">
              <Reveal delay={0.15}><Feature icon={CalendarClock} title="On-demand guards">Book a licensed officer for any showing or open house in seconds.</Feature></Reveal>
              <Reveal delay={0.2}><Feature icon={Radar} title="Live tracking & ETA">Watch your guard arrive in real time.</Feature></Reveal>
              <Reveal delay={0.25}><Feature icon={Siren} title="One-tap SOS">Instant panic alert broadcasts your location to dispatch.</Feature></Reveal>
              <Reveal delay={0.3}><Feature icon={ShieldCheck} title="Vetted every time">Every officer is licensed, insured, and background-checked.</Feature></Reveal>
            </div>
            <Reveal delay={0.35}>
              <button
                onClick={() => scrollTo("#waitlist")}
                data-testid="agents-cta-btn"
                className="group mt-10 font-head font-bold bg-[#D4AF37] text-[#050914] px-6 h-[52px] rounded-xl hover:bg-[#E4C569] transition-colors duration-200 inline-flex items-center gap-2"
              >
                Reserve My Spot
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              </button>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Companies */}
      <section id="companies" className="relative py-24 lg:py-32 bg-[#0F172A]/40 border-y border-[#1E293B] scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <Reveal><span className="kicker">For Security Companies</span></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-head font-black tracking-tight text-[#F8FAFC] text-4xl lg:text-5xl mt-5 leading-[1.05]">
                Fill your roster with <span className="text-[#D4AF37]">steady, qualified work.</span>
              </h2>
            </Reveal>
            <div className="mt-10 space-y-7">
              <Reveal delay={0.15}><Feature icon={TrendingUp} title="A stream of vetted job requests">Direct from local real estate professionals.</Feature></Reveal>
              <Reveal delay={0.2}><Feature icon={Wallet} title="Keep 75% of every assignment">Automated payouts and invoicing.</Feature></Reveal>
              <Reveal delay={0.25}><Feature icon={LayoutDashboard} title="Dispatch & manage">Run your entire team from one dashboard.</Feature></Reveal>
              <Reveal delay={0.3}><Feature icon={Sparkles} title="Grow your book of business">Without chasing leads.</Feature></Reveal>
            </div>
            <Reveal delay={0.35}>
              <button
                onClick={() => scrollTo("#waitlist")}
                data-testid="companies-cta-btn"
                className="group mt-10 font-head font-bold bg-transparent text-[#D4AF37] border-[1.5px] border-[#D4AF37] px-6 h-[52px] rounded-xl hover:bg-[#292414] transition-colors duration-200 inline-flex items-center gap-2"
              >
                Become a Founding Partner
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              </button>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <ClippedImage src={SECURITY_IMG} alt="Licensed security officer" />
          </Reveal>
        </div>
      </section>
    </div>
  );
};
